import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CubeIcon } from "@heroicons/react/24/outline";
import api from "../../../../utils/axios";
import ProductBreadcrumb from "../components/ProductBreadcrumb";
import ProductSearchBar from "../components/ProductSearchBar";
import ProductStatusBadge from "../components/ProductStatusBadge";
import ProductSummaryCard from "../components/ProductSummaryCard";
import ProductTableAction from "../components/ProductTableAction";

export default function TagProductsTable() {
    const { tagId } = useParams();
    const navigate = useNavigate();
    const [tag, setTag] = useState(null);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchTagProducts = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/tags/${tagId}/productsTable`);
            setTag(response.data);
        } catch (error) {
            console.log("Error al obtener productos de etiqueta:", error);
        } finally {
            setIsLoading(false);
        }
        };

        fetchTagProducts();
    }, [tagId]);

    const products = tag?.products || [];

    const filteredProducts = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        if (!normalizedSearch) return products;

        return products.filter((product) =>
        `${product.name || ""} ${product.code || ""} ${product.brand || ""}`
            .toLowerCase()
            .includes(normalizedSearch)
        );
    }, [products, search]);

    const openProduct = (productId) => {
        navigate(`/productos/new-table/tags/${tagId}/products/${productId}`);
    };

    const editProduct = (productId) => {
        navigate(`/productos/edit/${productId}`);
    };

    return (
        <div className="main-container">
        <div className="tw-flex tw-flex-col tw-gap-4 tw-px-3 tw-py-4">
            <div className="tw-flex tw-items-center tw-gap-3">
            <button
                type="button"
                onClick={() => navigate("/productos/new-table")}
                className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-bg-slate-50"
            >
                ← Volver
            </button>

            <ProductBreadcrumb
                items={[
                { label: "Etiquetas", to: "/productos/new-table" },
                { label: tag?.name || "Etiqueta" },
                ]}
            />
            </div>

            <ProductSearchBar value={search} onChange={setSearch} />

            {tag ? (
            <ProductSummaryCard
                title={tag.name}
                status={tag.status}
                items={[
                { label: "Proveedor", value: tag.provider?.name || "-" },
                { label: "Productos", value: `${tag.productCount || 0} productos` },
                ]}
                onEdit={() => navigate(`/tags/edit/${tagId}`)}
            />
            ) : null}

            <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse tw-text-sm">
                <thead>
                    <tr className="tw-border-b tw-border-slate-200 tw-bg-white tw-text-left tw-text-xs tw-font-semibold tw-text-slate-900">
                    <th className="tw-w-12 tw-px-5 tw-py-3">
                        <CubeIcon className="tw-h-4 tw-w-4 tw-text-slate-500" />
                    </th>
                    <th className="tw-px-5 tw-py-3">Producto</th>
                    <th className="tw-px-5 tw-py-3">Código</th>
                    <th className="tw-px-5 tw-py-3">Estado</th>
                    <th className="tw-px-5 tw-py-3">Marca</th>
                    <th className="tw-px-5 tw-py-3 tw-text-center">Cantidad</th>
                    <th className="tw-px-5 tw-py-3 tw-text-center">Lotes</th>
                    <th className="tw-px-5 tw-py-3"></th>
                    </tr>
                </thead>

                <tbody>
                    {isLoading ? (
                    <tr>
                        <td colSpan={8} className="tw-px-5 tw-py-8 tw-text-center tw-text-slate-500">
                        Cargando productos...
                        </td>
                    </tr>
                    ) : filteredProducts.length ? (
                    filteredProducts.map((product) => (
                        <tr
                        key={product._id}
                        onClick={() => openProduct(product._id)}
                        className="tw-cursor-pointer tw-border-b tw-border-slate-200 tw-transition last:tw-border-b-0 hover:tw-bg-slate-50"
                        >
                        <td className="tw-px-5 tw-py-3">
                            <span className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-cyan-50 tw-text-cyan-700">
                            <CubeIcon className="tw-h-4 tw-w-4" />
                            </span>
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-font-semibold tw-text-slate-950">
                            {product.name}
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-text-slate-600">
                            #{product.code}
                        </td>
                        <td className="tw-px-5 tw-py-3">
                            <ProductStatusBadge status={product.status} />
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-text-slate-600">
                            {product.brand || "-"}
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-text-center tw-font-semibold">
                            {product.quantity ?? 0}
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-text-center">
                            <span className="tw-inline-flex tw-min-w-7 tw-items-center tw-justify-center tw-rounded-lg tw-bg-slate-100 tw-px-2 tw-py-1 tw-font-semibold tw-text-slate-700">
                            {product.lotCount || 0}
                            </span>
                        </td>
                        <td className="tw-px-5 tw-py-3">
                            <ProductTableAction
                            onEdit={() => editProduct(product._id)}
                            onOpen={() => openProduct(product._id)}
                            />
                        </td>
                        </tr>
                    ))
                    ) : (
                    <tr>
                        <td colSpan={8} className="tw-px-5 tw-py-8 tw-text-center tw-text-slate-500">
                        No se encontraron productos
                        </td>
                    </tr>
                    )}
                </tbody>
                </table>
            </div>
            </div>
        </div>
        </div>
    );
}